import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  AlertTriangle,
  Skull,
  Calendar,
  ImageOff,
  RotateCcw,
} from "lucide-react";

/**
 * Phase 5 — Pinterest-style product card.
 *
 * Renders one product with the customer-requested layout:
 * - Image on top (16:9 ratio).
 * - Top-left status badge (NO SALES / OLD / DEAD / NO STOCK).
 * - Top-right margin % badge.
 * - Bottom-right "N in stock" pill on top of the image.
 * - Body: SKU · name · vendor.
 * - Mini-cards: retail / cost / total +N%.
 * - Red "capital tied up" box (qty × retail, visual only).
 * - Footer chip: product_type or category.
 * - If `qty_as_is > 0`, small grey "X devueltos" pill next to qty.
 */
function fmt$(n, fallback = "—") {
  if (n === null || n === undefined || Number.isNaN(n)) return fallback;
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

export default function ProductCard({ product }) {
  const { t } = useTranslation();
  const [imgErr, setImgErr] = useState(false);

  const {
    sku,
    name,
    vendor,
    product_type,
    image_url,
    local_image_url,
    genqty_image_url,
    retail_price,
    unit_cost,
    qty_in_stock = 0,
    qty_as_is = 0,
    margin_pct,
    total_plus_markup,
    capital_tied_up,
    card_capital_retail,
    is_dead,
    is_old,
    is_no_sales,
    no_stock,
    days_in_stock,
    days_in_stock_oldest,
    category,
  } = product;

  // Defensive fallback so old DB rows (before the backend cascade fix) still
  // render the GENQTY CDN photo when the primary `image_url` is null.
  const imageSrc = image_url || local_image_url || genqty_image_url;

  // Choose the strongest badge — dead > no-sales > old > no-stock.
  let statusBadge = null;
  if (is_dead) {
    statusBadge = {
      label: t("catalog.badges.dead"),
      icon: Skull,
      className: "bg-red-900/80 text-red-100 border-red-700",
    };
  } else if (is_no_sales) {
    statusBadge = {
      label: t("catalog.badges.noSales"),
      icon: AlertTriangle,
      className: "bg-red-500/80 text-white border-red-500",
    };
  } else if (is_old) {
    statusBadge = {
      label: t("catalog.badges.old"),
      icon: Calendar,
      className: "bg-amber-500/80 text-white border-amber-500",
    };
  } else if (no_stock) {
    statusBadge = {
      label: t("catalog.badges.noStock"),
      icon: Package,
      className: "bg-slate-600/80 text-slate-100 border-slate-500",
    };
  }

  return (
    <Card
      className="group relative flex flex-col overflow-hidden border border-border bg-card transition hover:border-primary/50 hover:shadow-lg"
      data-testid={`catalog-card-${sku}`}
    >
      {/* Image */}
      <Link
        to={`/catalog/${encodeURIComponent(sku)}`}
        className="relative block aspect-[4/3] w-full overflow-hidden bg-muted"
      >
        {imageSrc && !imgErr ? (
          <img
            src={imageSrc}
            alt={name || sku}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImgErr(true)}
            className="h-full w-full object-cover transition group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-muted/40">
            <ImageOff className="h-12 w-12 text-muted-foreground/40" />
          </div>
        )}

        {/* Top-left status */}
        {statusBadge && (
          <div className="absolute left-2 top-2">
            <Badge
              variant="outline"
              className={`gap-1 px-2 py-1 text-[9px] font-bold uppercase tracking-wider shadow ${statusBadge.className}`}
            >
              <statusBadge.icon className="h-3 w-3" />
              {statusBadge.label}
            </Badge>
          </div>
        )}

        {/* Top-right margin */}
        {margin_pct !== null && margin_pct !== undefined && (
          <div className="absolute right-2 top-2">
            <Badge className="border-emerald-400 bg-emerald-500/90 px-2 py-1 text-[10px] font-bold text-white shadow">
              {margin_pct.toFixed(1)}%
            </Badge>
          </div>
        )}

        {/* Bottom-right qty */}
        <div className="absolute bottom-2 right-2 flex items-center gap-1">
          {qty_as_is > 0 && (
            <Badge
              variant="outline"
              className="gap-1 border-slate-500/60 bg-slate-700/70 px-1.5 py-0.5 text-[9px] text-slate-100"
              title={t("catalog.badges.asIsTooltip")}
            >
              <RotateCcw className="h-2.5 w-2.5" />
              {t("catalog.badges.asIs", { n: qty_as_is })}
            </Badge>
          )}
          <Badge className="bg-slate-900/80 px-2 py-1 text-[10px] font-bold text-white shadow">
            {qty_in_stock} {t("catalog.card.inStock")}
          </Badge>
        </div>
      </Link>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div>
          <div className="font-mono text-[10px] text-muted-foreground">{sku}</div>
          <Link
            to={`/catalog/${encodeURIComponent(sku)}`}
            className="line-clamp-2 text-sm font-semibold leading-tight hover:underline"
            title={name}
          >
            {name || "—"}
          </Link>
          {vendor && (
            <div className="mt-0.5 text-[10px] text-muted-foreground">
              {vendor}
            </div>
          )}
        </div>

        {/* Mini-cards: retail / cost / total */}
        <div className="grid grid-cols-3 gap-1">
          <Mini
            label={t("catalog.card.retail")}
            value={fmt$(retail_price)}
            tone="default"
          />
          <Mini
            label={t("catalog.card.cost")}
            value={fmt$(unit_cost)}
            tone="muted"
          />
          <Mini
            label={t("catalog.card.totalMarkup")}
            value={fmt$(total_plus_markup)}
            tone="primary"
          />
        </div>

        {/* Capital tied up */}
        {qty_in_stock > 0 && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-2 py-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-semibold uppercase tracking-wider text-red-400">
                {t("catalog.card.capitalTied")}
              </span>
              <span className="text-sm font-bold text-red-300">
                {fmt$(card_capital_retail)}
              </span>
            </div>
            <div className="mt-0.5 text-[9px] text-muted-foreground">
              {t("catalog.card.realCost")}: {fmt$(capital_tied_up)}
            </div>
          </div>
        )}

        {/* Days in stock — visible on the "old" tab cards */}
        {days_in_stock !== null && days_in_stock !== undefined && qty_in_stock > 0 && (
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>
              📅 {t("catalog.card.daysInStock", { n: days_in_stock })}
            </span>
            {days_in_stock_oldest !== null &&
              days_in_stock_oldest !== days_in_stock && (
                <span title={t("catalog.card.oldestTooltip")}>
                  {t("catalog.card.oldestDays", { n: days_in_stock_oldest })}
                </span>
              )}
          </div>
        )}

        {/* Footer: product_type or category chip */}
        {(product_type || category) && (
          <div className="mt-auto pt-1">
            <Badge
              variant="outline"
              className="border-border bg-muted/40 px-1.5 py-0 text-[9px] uppercase tracking-wider text-muted-foreground"
            >
              {product_type || category}
            </Badge>
          </div>
        )}
      </div>
    </Card>
  );
}

function Mini({ label, value, tone = "default" }) {
  const cls = {
    default: "border-border bg-muted/40 text-foreground",
    muted: "border-border bg-muted/20 text-muted-foreground",
    primary: "border-primary/40 bg-primary/10 text-primary",
  }[tone];
  return (
    <div className={`rounded border px-1.5 py-1 ${cls}`}>
      <div className="text-[8px] font-semibold uppercase tracking-wider opacity-70">
        {label}
      </div>
      <div className="text-[11px] font-bold leading-tight">{value}</div>
    </div>
  );
}
